import { useEffect, useMemo, useState } from 'react';

import differenceWith from 'lodash/differenceWith';
import isEqual from 'lodash/isEqual';

import { LabelsService } from '@/utils/services/LabelsService';

import useLabels from '@/utils/hooks/swr/useLabels';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useAcl from '@/utils/hooks/user/useAcl';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import PropTypes from 'prop-types';

import { aclFeatures } from '@/lib/acl/aclFeatures';

import Icon from '@/components/base/Icon';
import { SelectButton } from '@/components/base/Select/SelectComponent';
import { SelectFilterComponent } from '@/components/base/SelectFilter/SelectFilterComponent';
import ActionTag from '@/components/base/Tag/ActionTag';
import FilterSectionTitle from '@/components/pages/myprofile/filters/FilterSectionTitle';

/**
 * Custom button
 */

const ButtonSelectLabels = ({ error, disabled, selected, text }) => {
    const { t } = useTranslation();
    return (
        <SelectButton selected={selected} disabled={disabled} error={error}>
            <div className="flex items-center space-x-3">
                <span className="block truncate text-icoBlack dark:text-white">
                    {text ?? t('commons:media.labels.select_label')}
                </span>
            </div>
        </SelectButton>
    );
};

const SelectLabels = ({
    resetFilters,
    stopResetFilters,
    setRequestParams,
    placeholder,
    noBorder = false,
    isLabel = true,
    placement = 'top'
}) => {
    const { hasAcl } = useAcl();
    const isLocked = !hasAcl(aclFeatures.content.campaign.read);
    const { activeSocialProfile } = useActiveSocialProfile();

    const { labels, setRequestParams: setLabelsRequestParams } = useLabels({
        activeSPID: activeSocialProfile?.id,
        options: {
            revalidateOnFocus: false,
            revalidateOnReconnect: false
        }
    }); // pass active sp only on FB an IG to avoid useless requests
    const [activeLabels, setActiveLabels] = useState([]);
    const labelsOptionsFiltered = differenceWith(labels, activeLabels, isEqual);

    useEffect(() => {
        setRequestParams((prevState) => {
            if (activeLabels.length > 0) {
                return {
                    ...prevState,
                    filter_labels: activeLabels.map((label) => label.label)
                };
            }
            delete prevState.filter_labels;
            return { ...prevState };
        });
    }, [activeLabels, setRequestParams]);

    const addLabel = (labelToAdd) => {
        if (activeLabels.filter((item) => labelToAdd.id === item.id).length === 0) {
            setActiveLabels((prevState) => [...prevState, labelToAdd]);
        }
    };

    const removeLabel = (labelToRemove) => {
        setActiveLabels((prevState) => prevState.filter((item) => labelToRemove.id !== item.id));
    };

    useEffect(() => {
        if (resetFilters) {
            setActiveLabels([]);
            stopResetFilters();
        }
    }, [resetFilters]);

    const debouncedSearch = useMemo(
        () => LabelsService.createDebouncedSearch(setLabelsRequestParams),
        [setLabelsRequestParams]
    );

    useEffect(() => {
        return () => {
            debouncedSearch?.cancel?.();
        };
    }, [debouncedSearch]);

    return (
        <div>
            {isLabel && (
                <FilterSectionTitle icon="labels" isLocked={isLocked}>
                    Labels
                </FilterSectionTitle>
            )}

            <SelectFilterComponent
                idInput="select-labels"
                button={({ selected, disabled, error }) => (
                    <ButtonSelectLabels
                        selected={selected}
                        disabled={disabled}
                        error={error}
                        text={placeholder}
                    />
                )}
                selected={null}
                setSelected={addLabel}
                options={labelsOptionsFiltered}
                panelOptions={{ placement: placement }}
                layoutOptions={{ noBorder: noBorder }}
                disabled={isLocked}
                onSearch={debouncedSearch}
            />

            <ul className="-ml-2 mt-3 flex flex-wrap">
                {activeLabels.map((label, index) => (
                    <li className="mb-2 ml-2" key={index}>
                        <ActionTag icon="labels">
                            <span className="mr-1">{label.label}</span>
                            <button
                                aria-label="delete-label"
                                className="leading-none"
                                onClick={() => removeLabel(label)}>
                                <Icon icon="close" className="align-bottom" />
                            </button>
                        </ActionTag>
                    </li>
                ))}
            </ul>
        </div>
    );
};

SelectLabels.propTypes = {
    labelOptions: PropTypes.array,
    resetFilters: PropTypes.bool,
    stopResetFilters: PropTypes.func,
    setRequestParams: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    noBorder: PropTypes.bool,
    isLabel: PropTypes.bool,
    placement: PropTypes.string
};

export default SelectLabels;
